import { getPageQueryURL } from '3-components/ListingApp/ListingFilters/ListingFilterHelpers';
import { useEffect, useRef } from 'react';

/**
 * Handles scroll-to-top and URL sync when the user changes page in a listing
 * component, covering two React Query scenarios:
 *
 * 1. Cache hit — React Query returns data instantly; `isFetching` stays `false`
 *    throughout (no true/false transition ever occurs). Scroll immediately since
 *    the DOM is already showing the correct data.
 *
 * 2. Network fetch — `isFetching` transitions true → false. Scrolling at the
 *    moment the page changes would start the animation while React is still
 *    re-rendering the new results; the DOM mutation mid-flight interrupts smooth
 *    scroll (the intermittent "won't scroll to top" bug seen on prod with a fast
 *    CDN cache). A pending flag defers the scroll until the fetch settles.
 *
 * @param page       - Current page index; the scroll and URL update trigger whenever this changes.
 * @param isFetching - React Query `isFetching` flag for the active query.
 * @returns A ref to attach to the element that should be scrolled into view.
 */
const useListingScroll = (page: number, isFetching: boolean): React.RefObject<HTMLDivElement> => {
    const scrollToEl = useRef<HTMLDivElement>(null);
    // Boolean ref (not state) to flag that a page change occurred and a scroll is pending.
    // Using a ref avoids triggering an extra re-render when the flag is toggled.
    const pendingScrollRef = useRef(false);
    // Tracks whether this is the initial mount. The [page] effect fires on mount too,
    // but we must not scroll on first render — only when the user actively changes page.
    const isMountedRef = useRef(false);

    useEffect(() => {
        window.history.replaceState({}, '', getPageQueryURL(page));

        if (!isMountedRef.current) {
            // First render: update the URL but skip scrolling.
            isMountedRef.current = true;
            return;
        }

        if (!isFetching) {
            // Cache hit: DOM already has the right data, scroll immediately.
            scrollToEl.current?.scrollIntoView({ behavior: 'smooth' });
        } else {
            // Network fetch in progress: defer scroll to the [isFetching] effect below.
            pendingScrollRef.current = true;
        }
    // isFetching intentionally omitted — we only want to read its current value at the
    // moment the page changes, not re-run this effect whenever isFetching changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        // Handles the network-fetch case: scroll once isFetching settles to false,
        // meaning the new results are fully rendered and the DOM is stable.
        if (!isFetching && pendingScrollRef.current) {
            pendingScrollRef.current = false;
            scrollToEl.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isFetching]);

    return scrollToEl;
};

export default useListingScroll;
